import { API_BASE } from '@/shared/services/api';
import { useTranslation } from "react-i18next";import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/shared/components/ui/card';
import { Button } from '@/shared/components/ui/button';
import { Input } from '@/shared/components/ui/input';
import { Badge } from '@/shared/components/ui/badge';
import { Switch } from '@/shared/components/ui/switch';
import { toast } from 'sonner';
import {
  MessageSquare,
  Settings,
  FileText,
  Phone,
  Link as LinkIcon,
  Copy,
  Eye,
  EyeOff,
  Plus,
  CheckCircle2,
  Clock,
  XCircle,
  ExternalLink,
  Shield,
  Loader2 } from
'lucide-react';

const API_URL = API_BASE;

// Default config structure
const defaultWhatsAppConfig = {
  enabled: true,
  businessId: '',
  phoneNumberId: '',
  displayName: 'EzERP Notifications',
  phoneNumber: '',
  accessToken: '',
  verificationStatus: 'pending',
  webhookUrl: '',
  messageQuota: { daily: 1000, used: 0, remaining: 1000 },
  templateStatus: { approved: 0, pending: 0, rejected: 0 }
};

export const WhatsAppSettings = () => {const { t } = useTranslation("comms");
  const [config, setConfig] = useState(defaultWhatsAppConfig);
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        setLoading(true);
        const response = await fetch(`${API_URL}/api/communications/settings/whatsapp`);
        if (response.ok) {
          const data = await response.json();
          setConfig((prev) => ({
            enabled: data.enabled ?? prev.enabled,
            businessId: data.accountSid || prev.businessId,
            phoneNumberId: data.fromNumber?.replace('whatsapp:', '') || prev.phoneNumberId,
            displayName: data.businessProfile?.name || prev.displayName,
            phoneNumber: data.fromNumber?.replace('whatsapp:', '') || prev.phoneNumber,
            accessToken: data.accountSid || prev.accessToken,
            verificationStatus: data.businessProfile?.verified ? 'verified' : 'pending',
            webhookUrl: prev.webhookUrl,
            messageQuota: prev.messageQuota,
            templateStatus: {
              approved: data.templates?.filter((t) => t.status === 'approved').length || 0,
              pending: data.templates?.filter((t) => t.status === 'pending').length || 0,
              rejected: data.templates?.filter((t) => t.status === 'rejected').length || 0
            }
          }));
        }
      } catch (err) {
        console.error('Failed to fetch WhatsApp settings:', err);
        toast.error('Failed to load WhatsApp settings');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, []);

  return (
    <div className="space-y-6" data-testid="communications-whatsapp">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-['Space_Grotesk'] flex items-center gap-3">
            <MessageSquare className="h-8 w-8 text-emerald-600" />{t("whatsappSettings", "WhatsApp Settings")}

          </h1>
          <p className="text-muted-foreground mt-1">{t("configureWhatsappBusiness", "Configure WhatsApp Business API for customer notifications")}

          </p>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-muted-foreground">{t("channelStatus", "Channel Status")}</span>
          <Switch checked={config.enabled} />
          <Badge className={config.enabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-700'}>
            {config.enabled ? 'Active' : 'Inactive'}
          </Badge>
        </div>
      </div>

      {/* Verification Status */}
      <Card className="card-elevated bg-gradient-to-r from-emerald-50 to-teal-50 dark:from-emerald-950/30 dark:to-teal-950/30">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/50">
                <Shield className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="font-semibold">{t("businessVerification", "Business Verification")}</p>
                <p className="text-sm text-muted-foreground">{t("metaBusinessAccount", "Meta Business Account verification status")}</p>
              </div>
            </div>
            <Badge className="bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400">
              <CheckCircle2 className="h-3 w-3 mr-1" />{t("verified", "Verified")}

            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* API Configuration */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings className="h-5 w-5 text-muted-foreground" />{t("cloudApiConfiguration", "Cloud API Configuration")}

          </CardTitle>
          <CardDescription>{t("configureYourWhatsapp", "Configure your WhatsApp Business Cloud API credentials from Meta Developer Portal")}

          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("businessAccountId", "Business Account ID")}</label>
              <Input
                value={config.businessId}
                placeholder="123456789012345"
                className="font-mono" />
              
              <p className="text-xs text-muted-foreground">{t("foundInMeta", "Found in Meta Business Suite settings")}

              </p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("phoneNumberId", "Phone Number ID")}</label>
              <Input
                value={config.phoneNumberId}
                placeholder="123456789012345"
                className="font-mono" />
              
              <p className="text-xs text-muted-foreground">{t("theIdOf", "The ID of your registered WhatsApp phone number")}

              </p>
            </div>
          </div>
          
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("displayName", "Display Name")}</label>
              <Input
                value={config.displayName}
                placeholder="Your Business Name" />
              
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">{t("phoneNumber", "Phone Number")}</label>
              <Input
                value={config.phoneNumber}
                placeholder="+1 (555) 123-4567"
                className="font-mono" />
              
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t("accessToken", "Access Token")}</label>
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Input
                  type={showToken ? 'text' : 'password'}
                  value={config.accessToken}
                  placeholder="EAAxxxxxxxxxxxxxxxxxxxxxxx"
                  className="pr-20 font-mono text-sm" />
                
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-8 top-0 h-full"
                  onClick={() => setShowToken(!showToken)}>
                  
                  {showToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute right-0 top-0 h-full">
                  
                  <Copy className="h-4 w-4" />
                </Button>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">{t("permanentAccessToken", "Permanent access token from Meta Developer Portal")}

            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium">{t("webhookUrl", "Webhook URL")}</label>
            <div className="flex gap-2">
              <Input
                value={config.webhookUrl}
                placeholder="https://api.yourdomain.com/webhooks/whatsapp"
                className="font-mono text-sm" />
              
              <Button variant="outline">
                <LinkIcon className="h-4 w-4 mr-2" />{t("verify", "Verify")}

              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Usage Stats */}
      <div className="grid gap-4 sm:grid-cols-2">
        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="text-lg">{t("messageQuota", "Message Quota")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t("dailyLimit", "Daily Limit")}</span>
                <span className="font-semibold">{(config.messageQuota?.daily ?? 0).toLocaleString()}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t("usedToday", "Used Today")}</span>
                <span className="font-semibold text-emerald-600">{config.messageQuota.used}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">{t("remaining", "Remaining")}</span>
                <span className="font-semibold">{config.messageQuota.remaining}</span>
              </div>
              <div className="pt-2">
                <div className="h-2 bg-muted rounded-full overflow-hidden">
                  <div
                    className="h-full bg-emerald-500 rounded-full"
                    style={{ width: `${config.messageQuota.used / config.messageQuota.daily * 100}%` }} />
                  
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {(config.messageQuota.used / config.messageQuota.daily * 100).toFixed(1)}{t("used", "% used")}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated">
          <CardHeader>
            <CardTitle className="text-lg">{t("templateStatus", "Template Status")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                  <span>{t("approved", "Approved")}</span>
                </div>
                <span className="font-semibold text-emerald-600">{config.templateStatus.approved}</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-amber-600" />
                  <span>{t("pending", "Pending")}</span>
                </div>
                <span className="font-semibold text-amber-600">{config.templateStatus.pending}</span>
              </div>
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-red-600" />
                  <span>{t("rejected", "Rejected")}</span>
                </div>
                <span className="font-semibold text-red-600">{config.templateStatus.rejected}</span>
              </div>
              <Button variant="outline" className="w-full mt-2">
                <FileText className="h-4 w-4 mr-2" />{t("manageTemplates", "Manage Templates")}

              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Meta Resources */}
      <Card className="card-elevated">
        <CardHeader>
          <CardTitle className="text-lg">{t("resources", "Resources")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 sm:grid-cols-3">
            <a href="https://developers.facebook.com/docs/whatsapp/cloud-api" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full justify-start gap-2">
                <ExternalLink className="h-4 w-4" />{t("cloudApiDocs", "Cloud API Docs")}

              </Button>
            </a>
            <a href="https://business.facebook.com/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full justify-start gap-2">
                <ExternalLink className="h-4 w-4" />{t("metaBusinessSuite", "Meta Business Suite")}

              </Button>
            </a>
            <a href="https://developers.facebook.com/" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full justify-start gap-2">
                <ExternalLink className="h-4 w-4" />{t("developerPortal", "Developer Portal")}

              </Button>
            </a>
          </div>
        </CardContent>
      </Card>

      {/* Save Button */}
      <div className="flex justify-end gap-3">
        <Button variant="outline">{t("cancel", "Cancel")}</Button>
        <Button className="bg-emerald-600 hover:bg-emerald-700">{t("saveChanges", "Save Changes")}</Button>
      </div>
    </div>);

};